import frLocale from "@fullcalendar/core/locales/fr";
import dayGridPlugin from "@fullcalendar/daygrid";
import FullCalendar from "@fullcalendar/react";
import { Loader } from "@logora/debate.progress.loader";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { Box, MenuItem, Stack, TextField, Typography } from "@mui/material";
import dayjs from "dayjs";
import React, { useState } from "react";
import { useGetList } from "react-admin";
import { AutocompleteField } from "../components/AutocompleteField";
import { SearchBar } from "../search/SearchBar";
import styles from "./Calendar.module.scss";
import { CalendarItem } from "./CalendarItem";

export const Calendar = () => {
	const calendarRef = React.useRef(null);

	const getInitialDate = () => {
		const today = dayjs();

		if (today.day() === 6) {
			// If today is a Saturday, return the next Monday
			return today.add(2, "day");
		}

		return today.startOf("week");
	};

	const [filter, setFilter] = useState({
		"timestampDebut.gte": getInitialDate().toISOString(),
		"timestampDebut.lte": getInitialDate()
			.endOf("week")
			.subtract(1, "day") // Go back to Friday
			.toISOString(),
		search: undefined,
	});

	const { data: reunions, isLoading } = useGetList("reunions", {
		filter,
		meta: {
			include: ["organeReunionRef", "organeDemandeurRef", "pointsOdj"],
		},
		sort: { field: "timestampDebut", order: "asc" },
		pagination: { page: 1, perPage: 1000 },
	});

	// Format reunions data for fullcalendar
	const events = reunions?.map((reunion) => ({
		id: reunion.uid,
		title: reunion.objet || reunion.pointsOdj?.[0].objet || "",
		start: reunion.timestampDebut,
		end: reunion.timestampFin || null,
		extendedProps: {
			...reunion,
		},
	}));

	const renderEventContent = (eventInfo) => {
		const organe =
			eventInfo.event.extendedProps?.organeReunionRef ||
			eventInfo.event.extendedProps?.organeDemandeurRef;

		return (
			<CalendarItem
				key={eventInfo.event.id}
				startTime={eventInfo.event.start}
				title={eventInfo.event.title}
				category={eventInfo?.event?.extendedProps?.xsiType}
				location={organe?.libelleTronque || organe?.libelle || ""}
				link={`#/parliament/reunions/${eventInfo.event.id}`}
			/>
		);
	};

	const renderDayHeader = (arg) => {
		return (
			<Stack alignItems="flex-start" padding={2} width="100%">
				<Typography textTransform="capitalize" fontSize="small">
					{dayjs(arg.date).format("dddd")}
				</Typography>

				<Typography
					textTransform="capitalize"
					fontSize="1.5rem"
					fontWeight="bold"
				>
					{dayjs(arg.date).date()}
				</Typography>
			</Stack>
		);
	};

	const handleDatesSet = ({ start: newStart, end: newEnd }) => {
		const currentStart = filter["timestampDebut.gte"];
		const currentEnd = filter["timestampDebut.lte"];

		if (
			currentStart !== newStart.toISOString() ||
			currentEnd !== newEnd.toISOString()
		) {
			setFilter({
				...filter,
				"timestampDebut.gte": newStart.toISOString(),
				"timestampDebut.lte": newEnd.toISOString(),
			});
		}
	};

	return (
		<div className={styles.calendarContainer}>
			<Toolbar
				calendarRef={calendarRef}
				filter={filter}
				setFilter={setFilter}
			/>
			{!isLoading ? (
				<FullCalendar
					ref={calendarRef}
					plugins={[dayGridPlugin]}
					initialView="dayGridWeek"
					headerToolbar={false}
					events={events}
					eventContent={renderEventContent}
					height="auto"
					initialDate={filter["timestampDebut.gte"]}
					dayMaxEvents={true}
					className={styles.calendar}
					locale={frLocale}
					weekends={false}
					datesSet={handleDatesSet}
					dayHeaderContent={renderDayHeader}
				/>
			) : (
				<Loader />
			)}
		</div>
	);
};

export const Toolbar = ({ calendarRef, filter, setFilter }) => {
	const start = dayjs(filter["timestampDebut.gte"]);
	const end = dayjs(filter["timestampDebut.lte"]).subtract(1, "day");

	const handleNav = (action) => {
		const calendarApi = calendarRef.current?.getApi();
		if (!calendarApi) return;

		switch (action) {
			case "prev":
				calendarApi.prev();
				break;
			case "next":
				calendarApi.next();
				break;
			case "today":
				calendarApi.today();
				break;
			default:
				break;
		}
	};

	const handleFilter = (newFilter) => {
		setFilter({ ...filter, ...newFilter });
	};

	const getWeekFormat = () => {
		const sameMonth = start.month() === end.month();
		const sameYear = start.year() === end.year();

		if (sameMonth && sameYear) {
			return `Semaine du ${start.format("D")} au ${end.format("D MMMM YYYY")}`;
		}
		if (sameYear) {
			return `Semaine du ${start.format("D MMMM")} au ${end.format(
				"D MMMM YYYY",
			)}`;
		}
		// Different years → Dec 30, 2024 – Jan 5, 2025
		return `Semaine du ${start.format("D MMMM YYYY")} au ${end.format(
			"D MMMM YYYY",
		)}`;
	};

	return (
		<Stack
			direction="row"
			alignItems="center"
			justifyContent="space-between"
			paddingBlock={5}
			spacing={1}
		>
			<Stack direction="row" gap={1}>
				<TextField
					label="Type"
					select
					SelectProps={{ autoWidth: true }}
					size="small"
					sx={{ minWidth: "12em" }}
					defaultValue="all"
					onChange={(e) =>
						handleFilter({
							xsiType: e.target.value === "all" ? undefined : e.target.value,
						})
					}
				>
					<MenuItem value="all">Tous</MenuItem>
					<MenuItem value="reunionCommission_type">Commission</MenuItem>
					<MenuItem value="seance_type">Débat en Séance</MenuItem>
					<MenuItem value="reunionInitParlementaire_type">Réunion</MenuItem>
				</TextField>

				<AutocompleteField
					resource="organes"
					label="Organe"
					getOptionLabel={(organe) => organe?.libelle}
					onChange={(value) =>
						handleFilter({
							organeReunionRefUid: value || undefined,
						})
					}
					searchOnType
					perPage={10}
					filters={{ actif: true }}
				/>
			</Stack>

			<Stack direction="row" alignItems="center" justifyContent="center">
				<button
					type="button"
					onClick={() => handleNav("prev")}
					className={styles.calendarNavBtn}
				>
					<ChevronLeft />
				</button>

				<Typography
					fontSize={20}
					fontWeight="semibold"
					marginInline={5}
					whiteSpace="nowrap"
				>
					{getWeekFormat()}
				</Typography>

				<button
					type="button"
					onClick={() => handleNav("next")}
					className={styles.calendarNavBtn}
				>
					<ChevronRight />
				</button>
			</Stack>

			<Box width="25%" marginInline="auto">
				<SearchBar
					placeholder="Rechercher"
					onSearchChange={(search) => handleFilter({ search })}
				/>
			</Box>
		</Stack>
	);
};
